import { Colors } from 'styles';

import { ReactComponent as MagicWandIcon } from 'assets/MagicWand.svg';
import Tooltip from 'common/ui/Tooltip';
import type { VirtualElement } from '@popperjs/core';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getHeadingNodeWithSynonym } from '../plugins/heading/utils/normalization';
import { useHeadingKeywords } from 'hooks/useHeadingKeywords';
import { HeadingLevel } from '../plugins';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { Editor } from 'slate';
import { logger } from 'modules/logger';
import { usePopper } from 'react-popper';
import styled, { css } from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import type { GenerateImpressionState } from 'hooks/useImpressionGenerator';
import { useImpressionGenerator } from 'hooks/useImpressionGenerator';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useUnifiedEditorElement } from './UnifiedEditorElementContext';
import { AutoImpressionRenderMethod } from 'domains/reporter/RichTextEditor/analytics/performance';
import { type RangeSnapshot, setReferenceElementIfRangeChanged } from './utils/rangeSnapshot';
import { useCoalescedAnimationFrame } from '../hooks/useCoalescedAnimationFrame';
import { FIELDS_WRAPPER_PADDING } from 'domains/reporter/Reporter/Editor/Editor.Wrapper';
import { useEditorWrapperElement } from 'domains/reporter/Reporter/Editor/EditorWrapperElementContext';

const ButtonWrapper = styled.div`
  margin-right: 0 !important;
`;

export const WAND_ICON_SIZE = 16;
export const WAND_GUTTER_OFFSET = Math.max((FIELDS_WRAPPER_PADDING - WAND_ICON_SIZE) / 2, 0);

const getButtonColor = (state: GenerateImpressionState): string => {
  switch (state) {
    case 'initial':
    case 'loading':
      return Colors.blue4;
    case 'visited':
      return Colors.gray10;
    case 'stale':
      return Colors.blue4;
    case 'error':
      return Colors.red4;
    default:
      return '';
  }
};

export const getTooltip = (state: GenerateImpressionState): string | null => {
  switch (state) {
    case 'visited':
    case 'initial':
      return 'Generate Impression';
    case 'loading':
      return null;
    case 'stale':
      return 'Generate Updated Impression';
    case 'error':
      return 'Impression generation unavailable due to connection error';
    default:
      return null;
  }
};

// Isolate the positioning logic from unrelated parent rerenders.
const GenerateButtonWithState = memo(function GenerateButtonWithState({
  generateImpressionState,
  generateAndStitchImpression,
}: {
  generateImpressionState: GenerateImpressionState;
  generateAndStitchImpression: () => void;
}) {
  const [referenceElement, setReferenceElement] = useState<Range | null>(null);
  const previousRangeSnapshot = useRef<Nullable<RangeSnapshot>>(null);
  const headingKeywords = useHeadingKeywords({ context: APOLLO_QUERY_CONTEXT });
  const editor = useSlateStatic();
  const editorWrapperElement = useEditorWrapperElement();
  const unifiedEditorElement = useUnifiedEditorElement();
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const popperReference: VirtualElement | null = useMemo(() => {
    if (referenceElement == null || editorWrapperElement == null) {
      // Wait for both anchors before rendering so the wand never flashes over the heading text.
      return null;
    }

    // Preserve the heading's scroll context for Popper.
    const contextElement =
      referenceElement.commonAncestorContainer instanceof Element
        ? referenceElement.commonAncestorContainer
        : referenceElement.commonAncestorContainer.parentElement;

    return {
      contextElement: contextElement ?? undefined,
      getBoundingClientRect: () => {
        const headingRect = referenceElement.getBoundingClientRect();
        const editorWrapperRect = editorWrapperElement.getBoundingClientRect();

        // Anchor X to the wrapper gutter and Y to the heading row.
        return {
          width: 0,
          height: headingRect.height,
          top: headingRect.top,
          bottom: headingRect.bottom,
          left: editorWrapperRect.left,
          right: editorWrapperRect.left,
          x: editorWrapperRect.left,
          y: headingRect.top,
          toJSON: () => ({}),
        };
      },
    };
  }, [editorWrapperElement, referenceElement]);
  const { styles, state } = usePopper(popperReference, popperElement, {
    placement: 'right',
    modifiers: [{ name: 'offset', options: { offset: [0, WAND_GUTTER_OFFSET] } }],
  });
  const popperStyles = styles.popper || {};
  const hasPopperOffsets = state?.modifiersData?.popperOffsets != null;
  const outOfView =
    (referenceElement?.getBoundingClientRect()?.bottom ?? 0) >
      (unifiedEditorElement?.getBoundingClientRect()?.bottom ?? 0) - 32 ||
    (referenceElement?.getBoundingClientRect()?.top ?? 0) <
      (unifiedEditorElement?.getBoundingClientRect()?.top ?? 0);
  const canGenerateImpression =
    generateImpressionState === 'initial' ||
    generateImpressionState === 'stale' ||
    generateImpressionState === 'visited';

  const updateReferenceElement = useCallback(() => {
    if (editor === null) {
      if (previousRangeSnapshot.current != null) {
        previousRangeSnapshot.current = null;
        setReferenceElement(null);
      }
      return;
    }

    const setReferenceElementIfChanged = (nextRange: globalThis.Range | null) =>
      setReferenceElementIfRangeChanged({
        nextRange,
        previousRangeSnapshotRef: previousRangeSnapshot,
        setReferenceElement,
      });

    const updateReferenceElementForHeading = () => {
      const impressionHeadingNode =
        getHeadingNodeWithSynonym(editor, headingKeywords.impressions, HeadingLevel.H1) ??
        getHeadingNodeWithSynonym(editor, headingKeywords.impressions, HeadingLevel.H2) ??
        Editor.last(editor, []);

      try {
        const domRange = ReactEditor.toDOMRange(
          editor,
          Editor.range(editor, impressionHeadingNode[1])
        );
        setReferenceElementIfChanged(domRange);
      } catch (e: any) {
        setReferenceElementIfChanged(null);
      }
    };

    updateReferenceElementForHeading();
  }, [editor, headingKeywords.impressions]);
  const { schedule: scheduleUpdateReferenceElement } =
    useCoalescedAnimationFrame(updateReferenceElement);

  useLayoutEffect(() => {
    updateReferenceElement();
    // Reposition only when relevant browser/editor events happen.
    document.addEventListener('selectionchange', scheduleUpdateReferenceElement);
    window.addEventListener('scroll', scheduleUpdateReferenceElement, true);
    window.addEventListener('resize', scheduleUpdateReferenceElement);

    return () => {
      document.removeEventListener('selectionchange', scheduleUpdateReferenceElement);
      window.removeEventListener('scroll', scheduleUpdateReferenceElement, true);
      window.removeEventListener('resize', scheduleUpdateReferenceElement);
    };
  }, [updateReferenceElement, scheduleUpdateReferenceElement]);

  if (referenceElement == null || editorWrapperElement == null) {
    return null;
  }

  return (
    <ButtonWrapper data-testid="impression-generator-tooltip">
      <Tooltip content={getTooltip(generateImpressionState)}>
        <div
          onMouseDown={(event) => {
            /* We want to keep the editor focused after this button is pressed,
               so that programmatic text selection in the editor has an effect.

              We accomplish this by using `onmousedown` with `.preventDefault()`, instead of `onclick`. */
            event.preventDefault();

            canGenerateImpression && generateAndStitchImpression();
          }}
          data-testid={`impression-generator-${generateImpressionState}`}
          ref={setPopperElement}
          {...(state?.attributes?.popper != null ? state.attributes.popper : {})}
          style={{
            margin: 0,
            ...popperStyles,
          }}
          css={css`
            background-color: transparent;
            display: ${hasPopperOffsets && !outOfView ? 'flex' : 'none'};
            align-items: center;
            z-index: 0;
            height: 35px;
            :hover {
              cursor: ${canGenerateImpression ? 'pointer' : 'default'};
            }
          `}
        >
          {generateImpressionState === 'loading' ? (
            <CircularProgress data-testid="loading-icon" size={16} />
          ) : (
            <MagicWandIcon
              data-testid="wand-icon"
              css={css`
                @keyframes pulse {
                  0% {
                    fill: ${Colors.blue4};
                  }
                  50% {
                    fill: ${Colors.gray10};
                  }
                  100% {
                    fill: ${Colors.blue4};
                  }
                }
              `}
              style={{
                animation: generateImpressionState === 'stale' ? 'pulse 2s infinite' : 'none',
                fill: `${getButtonColor(generateImpressionState)}`,
                height: '16px',
                width: '16px',
              }}
            />
          )}
        </div>
      </Tooltip>
    </ButtonWrapper>
  );
});

const ImpressionGeneratorButton = (): React.ReactElement | null => {
  const { generateImpressionState, generateAndStitchImpression, impressionGeneratorNotAllowed } =
    useImpressionGenerator();
  const editor = useSlateStatic();

  const handleGenerateImpression = useCallback(() => {
    if (editor == null) return;
    logger.info('[ImpressionGeneratorButton] Impression generation triggered by button click');
    generateAndStitchImpression(editor, false, {
      triggerMethod: AutoImpressionRenderMethod.WandIconClick,
    });
  }, [editor, generateAndStitchImpression]);

  useEffect(() => {
    logger.info(
      `[ImpressionGeneratorButton] Generate impression button state: ${generateImpressionState}`
    );
  }, [generateImpressionState]);

  if (impressionGeneratorNotAllowed) return null;

  return (
    <GenerateButtonWithState
      generateImpressionState={generateImpressionState}
      generateAndStitchImpression={handleGenerateImpression}
    />
  );
};

export default ImpressionGeneratorButton;

ImpressionGeneratorButton.displayName = 'ImpressionGeneratorButton';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ImpressionGeneratorButton);
